import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { auth } from '../config/firebase'
import { checkAndSetupNewUser } from '../services/accountService'
import type { AccountDocument, ProfileDocument } from '../types/account'
import { USER_ID_KEY, clearPerUserCache } from '../utils/cacheUtils'
import { tokenService } from '../services/tokenService'
import OAuthToSModal from '../components/Legal/OAuthToSModal'
import ToSUpdateBanner from '../components/Legal/ToSUpdateBanner'
import GeneticConsentBanner from '../components/Legal/GeneticConsentBanner'
import { PendingDeletionBanner } from '../components/Legal/PendingDeletionBanner'
import { CURRENT_TOS_VERSION, CURRENT_GENETIC_CONSENT_VERSION } from '../config/legalVersions'
import {
  GENETIC_CONSENT_ACCEPTED_EVENT,
  GENETIC_CONSENT_REQUIRED_EVENT,
} from '../services/legalService'
import { API_BASE_URL } from '../config/api'
import { makeAuthenticatedRequest } from '../utils/apiInterceptor'

// Auth context interface
interface AuthContextType {
  currentUser: User | null
  userAccount: AccountDocument | null
  defaultProfile: ProfileDocument | null
  loading: boolean
  isAuthenticated: boolean
  isNewUser: boolean
  setupError: boolean
  isAuthReady: boolean
  isTosModalOpen: boolean
  refreshAccount: () => Promise<void>
  reloadAccountFromFirestore: () => Promise<void>
  patchUserAccount: (patch: Partial<AccountDocument>) => void
  requestOnboardingIntentPicker: () => void
  onboardingPickerRequestId: number
}

// Create auth context
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Auth provider props
interface AuthProviderProps {
  children: ReactNode
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  useEffect(() => {
    console.log('[AuthProvider] Mounted')
    return () => console.log('[AuthProvider] Unmounted')
  }, [])

  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userAccount, setUserAccount] = useState<AccountDocument | null>(null)
  const [defaultProfile, setDefaultProfile] = useState<ProfileDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [isNewUser, setIsNewUser] = useState(false)
  const [setupError, setSetupError] = useState(false)
  const [isAuthReady, setIsAuthReady] = useState(false)
  // If the backend was down during initial setup attempts, make a one-time
  // recovery attempt in the background after auth is established.
  const [recoveryAttempted, setRecoveryAttempted] = useState(false)
  // Single-flight guard to prevent concurrent setup calls for the same user
  const [setupInFlightUid, setSetupInFlightUid] = useState<string | null>(null)
  // ToS-related state
  const [showOAuthToSModal, setShowOAuthToSModal] = useState(false)
  const [showToSUpdateBanner, setShowToSUpdateBanner] = useState(false)
  const [showGeneticConsentBanner, setShowGeneticConsentBanner] = useState(false)

  // The consent gate on genetic uploads/kit registration (documents.py
  // confirm_upload, dna_kits.py register_kit) fires this when a user without
  // current consent tries to add genetic data. Force the banner open here
  // rather than waiting for the next login, since the user is mid-action.
  useEffect(() => {
    const onGeneticConsentRequired = () => setShowGeneticConsentBanner(true)
    window.addEventListener(GENETIC_CONSENT_REQUIRED_EVENT, onGeneticConsentRequired)
    return () => window.removeEventListener(GENETIC_CONSENT_REQUIRED_EVENT, onGeneticConsentRequired)
  }, [])
  const [onboardingPickerRequestId, setOnboardingPickerRequestId] = useState(0)

  useEffect(() => {
    // Purge per-user cache when authenticated user changes
    const prevUid = localStorage.getItem(USER_ID_KEY)
    if (prevUid && prevUid !== currentUser?.uid) {
      clearPerUserCache()
    }
    if (currentUser) {
      localStorage.setItem(USER_ID_KEY, currentUser.uid)
    } else {
      localStorage.removeItem(USER_ID_KEY)
    }
  }, [currentUser])

  useEffect(() => {
    // Subscribe to auth state changes
    // We need to set up listeners for BOTH auth instances and use the right one
    
    const setupAuthListeners = async () => {
      const { getViewAsAuth } = await import('../config/firebase')
      const viewAsAuth = getViewAsAuth()
      
      // Check if we're already in view-as mode (from sessionStorage only)
      // Don't set the flag based on URL alone - let ViewAsPage set it after verifying admin auth
      const isViewAsFromStorage = sessionStorage.getItem('viewAsMode') === 'true'
      let defaultAuthUid: string | null | undefined
      let viewAsAuthUid: string | null | undefined
      
      // Handler for default auth changes
      const handleDefaultAuthChange = async (user: any) => {
        // Only check storage, not URL, to determine if we're in view-as mode
        const isViewAsModeNow = sessionStorage.getItem('viewAsMode') === 'true'
        
        // If we're in view-as mode, ignore default auth changes
        if (isViewAsModeNow) {
          return
        }

        const nextUid = user?.uid ?? null
        if (defaultAuthUid === nextUid) {
          return
        }
        defaultAuthUid = nextUid
        
        await processAuthChange(user)
      }
      
      // Handler for view-as auth changes
      const handleViewAsAuthChange = async (user: any) => {
        // ONLY check storage, NOT URL - this prevents race conditions with persisted sessions
        // The flag will be set by ViewAsPage AFTER it verifies admin auth
        const isViewAsModeNow = sessionStorage.getItem('viewAsMode') === 'true'
        
        // Only process if we're actually in view-as mode (flag must be explicitly set)
        if (!isViewAsModeNow) {
          return
        }

        const nextUid = user?.uid ?? null
        if (viewAsAuthUid === nextUid) {
          return
        }
        viewAsAuthUid = nextUid
        
        await processAuthChange(user)
      }
      
      // Shared processing logic
      const processAuthChange = async (user: any) => {
        
        try {
          if (user) {
            // User is signed in - check and setup account/profile if needed
            setLoading(true)
            
            // Check if we're in View As mode - skip setup-user call for impersonated users
            // The target user already exists; calling setup-user would hit rate limits unnecessarily
            const isViewAsModeNow = sessionStorage.getItem('viewAsMode') === 'true'
            if (isViewAsModeNow) {
              console.log('[AuthContext] View As mode detected - skipping setup-user, loading account directly')
              // For View As mode, just load the existing account and profile directly
              try {
                const { getAccountDocument, getAllProfilesForUser } = await import('../services/accountService')
                const existingAccount = await getAccountDocument(user.uid)
                
                if (existingAccount) {
                  const result = await getAllProfilesForUser(user.uid)
                  const accessible = result.profiles
                  
                  // Sort by last accessed (most recent first)
                  const sortedByLastAccessed = [...accessible].sort((a, b) => {
                    const aLastAccessed = a.lastViewed || a.updatedAt || a.createdAt
                    const bLastAccessed = b.lastViewed || b.updatedAt || b.createdAt
                    const aTime = aLastAccessed instanceof Date ? aLastAccessed.getTime() : new Date(aLastAccessed).getTime()
                    const bTime = bLastAccessed instanceof Date ? bLastAccessed.getTime() : new Date(bLastAccessed).getTime()
                    return bTime - aTime
                  })
                  
                  setCurrentUser(user)
                  setUserAccount(existingAccount)
                  setDefaultProfile(sortedByLastAccessed[0] || null)
                  setIsNewUser(false)
                  setSetupError(false)
                  setIsAuthReady(true)
                  setLoading(false)
                  return
                }
              } catch (viewAsErr) {
                console.warn('[AuthContext] View As mode account load failed:', viewAsErr)
                // Fall through to set user without account data
                setCurrentUser(user)
                setUserAccount(null)
                setDefaultProfile(null)
                setIsNewUser(false)
                setSetupError(true)
                setIsAuthReady(false)
                setLoading(false)
                return
              }
            }
            
            // Don't run primary setup if recovery is already handling it
            if (recoveryAttempted) {
              setCurrentUser(user)
              setLoading(false)
              return
            }

            // Invalidate server-side plan/feature caches to reflect latest plan on login
            try {
              const token = await tokenService.forceRefresh()
              // Wait for token to propagate through Firebase SDK
              await new Promise(resolve => setTimeout(resolve, 500))
              await fetch(`${API_BASE_URL}/api/v1/cache/invalidate-account`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
              })
            } catch (_) { /* non-fatal */ }

            // Add retry logic for setup to prevent repeated failures
            let setupResult
            let retryCount = 0
            const maxRetries = 3

            while (retryCount < maxRetries) {
              try {
                // Debounce: if a setup call for this uid is already in-flight, wait for it to finish
                if (setupInFlightUid && setupInFlightUid === user.uid) {
                  // simple wait-and-retry loop until in-flight clears or retries exhausted
                  await new Promise(resolve => setTimeout(resolve, 200))
                  continue
                }
                setSetupInFlightUid(user.uid)
                setupResult = await checkAndSetupNewUser(user)
                break // Success, exit retry loop
              } catch (setupError) {
                retryCount++
                console.warn(`Setup attempt ${retryCount} failed:`, setupError)
                
                if (retryCount >= maxRetries) {
                  throw setupError // Re-throw after max retries
                }
                
                // Wait before retry (exponential backoff: 1s, 2s, 4s)
                const delay = Math.pow(2, retryCount - 1) * 1000
                await new Promise(resolve => setTimeout(resolve, delay))
              } finally {
                // Clear single-flight flag if we were the caller for this uid
                if (setupInFlightUid === user.uid) {
                  setSetupInFlightUid(null)
                }
              }
            }
            
            // Update all state atomically after setup completes
            setCurrentUser(user)
            setUserAccount(setupResult.account || null)
            setDefaultProfile(setupResult.profile || null)
            setIsNewUser(setupResult.isNewUser)
            setSetupError(false)
            setIsAuthReady(true)
            
            // Handle ToS acceptance for new OAuth users and migrated users
            // Show TOS modal if:
            // 1. User is new OAuth user (not email signup) who hasn't accepted TOS, OR
            // 2. User is a migrated user (is_legacy_member) who hasn't accepted TOS
            const compliance = setupResult.account?.compliance

            // Legacy migrated users who have not accepted current terms see the full modal.
            const needsLegacyTosAcceptance =
              setupResult.account?.isLegacyMember && !compliance?.termsAcceptedAt
            
            if (needsLegacyTosAcceptance) {
              setShowOAuthToSModal(true)
            }
            
            // Prompt existing users when their recorded version is behind current.
            if (
              compliance?.termsAcceptedAt &&
              compliance?.termsVersion &&
              compliance.termsVersion !== CURRENT_TOS_VERSION
            ) {
              setShowToSUpdateBanner(true)
            }

            // Genetic data consent is given separately from Terms and Privacy,
            // but it is only shown to accounts that already hold genetic data —
            // flagged once by the backfill script for pre-existing data. Users
            // with no genetic data are never prompted here; consent for *new*
            // genetic data is enforced at the point of upload or kit
            // registration instead, which is where it actually applies.
            if (
              compliance?.needsGeneticConsentBackfill &&
              compliance?.geneticDataConsentVersion !== CURRENT_GENETIC_CONSENT_VERSION
            ) {
              setShowGeneticConsentBanner(true)
            }
            
            // Start token monitoring for authenticated users
            tokenService.startMonitoring()
          } else {
            // User is signed out - clear all state and token cache
            tokenService.clearCache()
            tokenService.stopMonitoring()
            setCurrentUser(null)
            setUserAccount(null)
            setDefaultProfile(null)
            setIsNewUser(false)
            setSetupError(false)
            setIsAuthReady(false)
          }
        } catch (error) {
          console.error('Failed to setup user after all retries:', error)
          // Set user but clear account data on error
          setCurrentUser(user)
          setUserAccount(null)
          setDefaultProfile(null)
          setIsNewUser(false)
          setSetupError(true)
          setIsAuthReady(false)
        } finally {
          setLoading(false)
        }
      }
      
      // Set up listeners for BOTH auth instances with separate handlers
      const unsubscribeDefault = onAuthStateChanged(auth, handleDefaultAuthChange, (error) => {
        console.error('Default auth state change error:', error)
      })

      const unsubscribeViewAs = onAuthStateChanged(viewAsAuth, (user) => {
        handleViewAsAuthChange(user)
      }, (error) => {
        console.error('View-as auth state change error:', error)
      })

      // Safety valve: if Firebase never fires onAuthStateChanged (e.g. corrupted
      // IndexedDB persistence), unblock the app after 4 seconds so public routes load.
      const authTimeout = setTimeout(() => {
        setLoading(false)
      }, 4000)

      return () => {
        unsubscribeDefault()
        unsubscribeViewAs()
        clearTimeout(authTimeout)
      }
    }
    
    // Call the async setup and store cleanup function
    let cleanup: (() => void) | undefined
    setupAuthListeners().then(cleanupFn => {
      cleanup = cleanupFn
    })

    // Cleanup subscriptions on unmount
    return () => {
      if (cleanup) cleanup()
    }
  }, [])

  // Background recovery: if user is authenticated but account didn't load
  // (e.g., backend was temporarily unavailable during initial attempts),
  // try setup-user once more without toggling the global loading state.
  useEffect(() => {
    const runRecovery = async () => {
      if (!currentUser || userAccount || loading || recoveryAttempted) return

      // Don't run recovery if there's already a setup in progress
      if (setupInFlightUid && setupInFlightUid === currentUser.uid) {
        return
      }

      setRecoveryAttempted(true)
      try {
        setSetupInFlightUid(currentUser.uid)
        const setupResult = await checkAndSetupNewUser(currentUser)
        setUserAccount(setupResult.account || null)
        setDefaultProfile(setupResult.profile || null)
        setSetupError(false)
        setIsNewUser(setupResult.isNewUser)
      } catch (err) {
        console.warn('Recovery setup-user attempt failed:', err)
      } finally {
        if (setupInFlightUid === currentUser?.uid) {
          setSetupInFlightUid(null)
        }
      }
    }
    runRecovery()
  }, [currentUser, userAccount, loading, recoveryAttempted, setupInFlightUid])

  // Refresh account data - useful after migration completes
  // Wrapped in useCallback to prevent infinite loops in components that depend on it
  const refreshAccount = useCallback(async () => {
    if (!currentUser) return
    
    try {
      const setupResult = await checkAndSetupNewUser(currentUser)
      setUserAccount(setupResult.account || null)
      setDefaultProfile(setupResult.profile || null)
    } catch (error) {
      console.error('❌ [AuthContext] Failed to refresh account:', error)
    }
  }, [currentUser])

  // Reload account/profile from Firestore only — no backend setup-user call.
  // Use after local Firestore updates (onboarding reset, intent pick, dismiss).
  const reloadAccountFromFirestore = useCallback(async () => {
    if (!currentUser) return

    try {
      const { getAccountDocument, getAllProfilesForUser } = await import('../services/accountService')
      const account = await getAccountDocument(currentUser.uid)
      if (!account) return

      const result = await getAllProfilesForUser(currentUser.uid)
      const sortedByLastAccessed = [...result.profiles].sort((a, b) => {
        const aLastAccessed = a.lastViewed || a.updatedAt || a.createdAt
        const bLastAccessed = b.lastViewed || b.updatedAt || b.createdAt
        const aTime = aLastAccessed instanceof Date ? aLastAccessed.getTime() : new Date(aLastAccessed).getTime()
        const bTime = bLastAccessed instanceof Date ? bLastAccessed.getTime() : new Date(bLastAccessed).getTime()
        return bTime - aTime
      })

      setUserAccount(account)
      setDefaultProfile(sortedByLastAccessed[0] || null)
    } catch (error) {
      console.error('❌ [AuthContext] Failed to reload account from Firestore:', error)
    }
  }, [currentUser])

  const patchUserAccount = useCallback((patch: Partial<AccountDocument>) => {
    setUserAccount((prev) => (prev ? { ...prev, ...patch } : prev))
  }, [])

  const requestOnboardingIntentPicker = useCallback(() => {
    setOnboardingPickerRequestId((id) => id + 1)
  }, [])

  const value: AuthContextType = {
    currentUser,
    userAccount,
    defaultProfile,
    loading,
    isAuthenticated: !!currentUser,
    isNewUser,
    setupError,
    isAuthReady,
    isTosModalOpen: showOAuthToSModal,
    refreshAccount,
    reloadAccountFromFirestore,
    patchUserAccount,
    requestOnboardingIntentPicker,
    onboardingPickerRequestId,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}

      {currentUser && <PendingDeletionBanner onCancelled={refreshAccount} />}
      
      {/* ToS Modals */}
      <OAuthToSModal
        isOpen={showOAuthToSModal}
        onAccept={async () => {
          try {
            await Promise.all([
              makeAuthenticatedRequest('/api/v1/legal/accept-terms', {
                method: 'POST',
                body: JSON.stringify({ acceptance_method: 'update' }),
              }),
              makeAuthenticatedRequest('/api/v1/legal/accept-privacy-policy', {
                method: 'POST',
                body: JSON.stringify({ acceptance_method: 'update' }),
              }),
            ])
            // Refresh account data to get updated compliance info
            await refreshAccount()
            setShowOAuthToSModal(false)
          } catch (error) {
            console.error('Failed to accept Terms of Service:', error)
            // Still close modal to avoid blocking user, but log error
            setShowOAuthToSModal(false)
          }
        }}
        onClose={() => setShowOAuthToSModal(false)}
      />
      
      <ToSUpdateBanner
        isVisible={showToSUpdateBanner}
        onAccept={async () => {
          try {
            await Promise.all([
              makeAuthenticatedRequest('/api/v1/legal/accept-terms', {
                method: 'POST',
                body: JSON.stringify({ acceptance_method: 'update' }),
              }),
              makeAuthenticatedRequest('/api/v1/legal/accept-privacy-policy', {
                method: 'POST',
                body: JSON.stringify({ acceptance_method: 'update' }),
              }),
            ])
            // Refresh account data to get updated compliance info
            await refreshAccount()
            setShowToSUpdateBanner(false)
          } catch (error) {
            console.error('Failed to accept Terms of Service:', error)
          }
        }}
        userVersion={userAccount?.compliance?.termsVersion}
      />

      <GeneticConsentBanner
        isVisible={showGeneticConsentBanner && !showToSUpdateBanner && !showOAuthToSModal}
        onAccept={async () => {
          try {
            await makeAuthenticatedRequest('/api/v1/legal/accept-genetic-consent', {
              method: 'POST',
              body: JSON.stringify({ acceptance_method: 'update' }),
            })
            await refreshAccount()
            setShowGeneticConsentBanner(false)
            window.dispatchEvent(new CustomEvent(GENETIC_CONSENT_ACCEPTED_EVENT))
          } catch (error) {
            console.error('Failed to record genetic data consent:', error)
          }
        }}
      />
    </AuthContext.Provider>
  )
}

export default AuthProvider 
